import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';

// GlobalLoadingBar — top-of-page progress bar + optional blocking overlay.
// It tracks the number of in-flight axios requests via interceptors and
// animates a bar from left→right while requests are pending. When the very
// first page load is happening (before the user has ever seen the app),
// we also render a full-screen overlay so the user cannot interact with
// half-loaded UI. Once the initial wave settles, the overlay dismisses
// and only the thin top bar remains as an unobtrusive activity indicator.
export default function GlobalLoadingBar({ initialLoading = false }) {
  const [pending, setPending] = useState(0);
  const [progress, setProgress] = useState(0);
  const [visible, setVisible] = useState(false);
  const [firstWaveDone, setFirstWaveDone] = useState(false);
  const pendingRef = useRef(0);
  const progressTimerRef = useRef(null);

  useEffect(() => {
    const bump = (delta) => {
      pendingRef.current = Math.max(0, pendingRef.current + delta);
      setPending(pendingRef.current);
    };
    const reqId = axios.interceptors.request.use(
      (cfg) => { if (!cfg.skipGlobalLoading) bump(1); return cfg; },
      (err) => { if (!err.config?.skipGlobalLoading) bump(-1); return Promise.reject(err); }
    );
    const resId = axios.interceptors.response.use(
      (res) => { if (!res.config?.skipGlobalLoading) bump(-1); return res; },
      (err) => { if (!err.config?.skipGlobalLoading) bump(-1); return Promise.reject(err); }
    );
    return () => {
      axios.interceptors.request.eject(reqId);
      axios.interceptors.response.eject(resId);
    };
  }, []);

  // Drive the animated progress bar based on pending count.
  useEffect(() => {
    if (progressTimerRef.current) {
      clearInterval(progressTimerRef.current);
      progressTimerRef.current = null;
    }
    if (pending > 0 || initialLoading) {
      setVisible(true);
      setProgress((p) => (p < 15 ? 15 : p));
      progressTimerRef.current = setInterval(() => {
        setProgress((p) => {
          // Approach 90% asymptotically while requests are in flight.
          if (p >= 90) return p;
          const step = p < 50 ? 4 : p < 75 ? 2 : 1;
          return Math.min(90, p + step);
        });
      }, 200);
    } else if (visible) {
      // No pending requests → snap to 100% then fade out.
      setProgress(100);
      const t = setTimeout(() => {
        setVisible(false);
        setProgress(0);
        setFirstWaveDone(true);
      }, 350);
      return () => clearTimeout(t);
    }
    return () => {
      if (progressTimerRef.current) clearInterval(progressTimerRef.current);
    };
  }, [pending, initialLoading, visible]);

  // Full-screen blocking overlay only for the INITIAL app load. Once the
  // first request wave completes, subsequent loads only show the thin
  // top bar so users can keep interacting with the app.
  const showBlockingOverlay = (initialLoading || !firstWaveDone) && visible;

  return (
    <>
      {visible && (
        <div
          className="fixed top-0 left-0 right-0 z-[9999] h-1 bg-transparent pointer-events-none"
          data-testid="global-loading-bar"
        >
          <div
            className="h-full bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 transition-[width] duration-200 ease-out"
            style={{ width: `${progress}%`, boxShadow: '0 0 8px rgba(129, 140, 248, 0.6)' }}
          />
        </div>
      )}

      {showBlockingOverlay && (
        <div
          className="fixed inset-0 z-[9998] bg-white/70 backdrop-blur-sm flex items-center justify-center"
          data-testid="global-loading-overlay"
        >
          <div className="flex flex-col items-center gap-3">
            <div className="w-64 h-2 bg-slate-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 transition-[width] duration-200 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>
            <div className="text-slate-700 text-sm font-medium">Loading — please wait…</div>
            <div className="text-slate-400 text-xs">{progress}%</div>
          </div>
        </div>
      )}
    </>
  );
}
